import { isAfter, startOfDay } from 'date-fns';
import { atom } from 'jotai';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';

import type {
  PatientListModes,
  PatientListSortFilterEnum,
  SelectJumpToCurrentApptType,
  SelectedCountsFilter,
} from '../../@types/appointments';
import {
  RecentVisitsSortFilter,
  SelectJumpToCurrentAppt,
  SelectedUserExperience,
  SortFilterEnum,
} from '../../@types/appointments';
import type {
  AppointmentPatient,
  AppointmentProvider,
} from '../../react-query/useAppointments';

export const INITIAL_SELECTED_ANALYSES_STATE = {
  selectedAnalyses: [],
  unplannedQuadrantToothNumbers: [],
  patientId: undefined,
  findingType: undefined,
  anchorEl: null,
};
export type PatientListView = 'HUDDLE' | 'REVIEW' | 'MICRO';

// Typed storage for each atom
const modeStorage = createJSONStorage<PatientListModes>(() => sessionStorage);
const sortFilterStorage = createJSONStorage<PatientListSortFilterEnum>(
  () => sessionStorage,
);
const operatoryFilterStorage = createJSONStorage<string[]>(
  () => sessionStorage,
);
const searchStorage = createJSONStorage<string>(() => sessionStorage);
const selectedCountsFilterStorage = createJSONStorage<
  SelectedCountsFilter | undefined
>(() => sessionStorage);
const selectedDateStorage = createJSONStorage<Date | null>(
  () => sessionStorage,
);
const selectedUserExperienceStorage = createJSONStorage<string | null>(
  () => localStorage,
);
const jumpToCurrentApptStorage = createJSONStorage<string>(() => localStorage);

const selectedUnifiedIdStorage = createJSONStorage<string | null>(
  () => sessionStorage,
);
const appointmentPatientsStorage = createJSONStorage<AppointmentPatient[]>(
  () => localStorage,
);
// Base atoms
// Mode tracks if we are on the single daily view or if we are on the recent visits/search view
export const modeAtom = atomWithStorage(
  'jotai-mode',
  'RECENT_VISITS',
  modeStorage,
);
export const sortFilterAtom = atomWithStorage<PatientListSortFilterEnum>(
  'jotai-sort-filter',
  RecentVisitsSortFilter.APPOINTMENT_TIME_LATEST,
  sortFilterStorage,
);
export const operatoryFilterAtom = atomWithStorage<string[]>(
  'jotai-operatory-filter',
  [],
  operatoryFilterStorage,
);
const selectedProvidersStorage = createJSONStorage<AppointmentProvider[]>(
  () => sessionStorage,
);
export const selectedProvidersAtom = atomWithStorage<AppointmentProvider[]>(
  'jotai-selected-providers',
  [],
  selectedProvidersStorage,
);
const baseSearchAtom = atomWithStorage('jotai-search', '', searchStorage);
const selectedDateAtom = atomWithStorage<Date | null>(
  'jotai-selected-date',
  null,
  selectedDateStorage,
);
const selectedUserExperienceAtom = atomWithStorage(
  'jotai-selected-view',
  SelectedUserExperience.Default,
  selectedUserExperienceStorage,
);
export const patientListViewAtom = atom<PatientListView>('HUDDLE');
export const selectedCountsFilterAtom = atomWithStorage(
  'jotai-selected-counts-filter',
  undefined,
  selectedCountsFilterStorage,
);
export const jumpToCurrentApptAtom = atomWithStorage(
  'jotai-jump-to-current-appt',
  SelectJumpToCurrentAppt.Default,
  jumpToCurrentApptStorage,
);
export const unifiedIdAtom = atomWithStorage(
  'jotai-unified-id',
  '',
  selectedUnifiedIdStorage,
);
export const appointmentPatientsAtom = atomWithStorage(
  'jotai-appointment-patients',
  [],
  appointmentPatientsStorage,
  { getOnInit: true },
);

export type QuadrantNumber = 1 | 2 | 3 | 4;

const selectedTeethAtom = atom<string[] | null>([]);
export const defaultSelectedTeethAtom = atom<string[] | null>([]);

const userSelectedTeethAtom = atom<string[] | null>([]);

const selectedQuadrantsAtom = atom<QuadrantNumber[]>([]);
export const vaultAppointmentsAtom = atom<boolean | undefined>(undefined);

export const searchAllPracticesAtom = atom<boolean>(false);

export const searchAtom = atom(
  (get) => get(baseSearchAtom),
  (_, set, searchString: string) => {
    set(baseSearchAtom, searchString);
    if (searchString) {
      set(selectedCountsFilterAtom, undefined);
      set(modeAtom, 'RECENT_VISITS');
      set(selectedDateAtom, null);
      set(sortFilterAtom, SortFilterEnum.APPOINTMENT_TIME_LATEST);
      set(operatoryFilterAtom, []);
    } else {
      // When search is cleared, treat it like clicking "back to today"
      set(selectDateAtom, startOfDay(new Date()));
      set(selectedProvidersAtom, []);
      set(modeAtom, 'DAILY_HUDDLE');
      set(operatoryFilterAtom, []);
    }
  },
);
export const selectDateAtom = atom(
  (get) => {
    const selectedDate = get(selectedDateAtom);
    // If we get the selected date from storage, we need to parse it into a Date object
    if (typeof selectedDate === 'string') {
      return new Date(selectedDate);
    }
    return selectedDate;
  },
  (get, set, selectedDate: Date | null) => {
    if (!selectedDate) {
      return;
    }
    set(
      selectedUserExperienceAtom,
      isAfter(selectedDate, new Date()) && get(patientListViewAtom) === 'REVIEW'
        ? SelectedUserExperience.Default
        : get(selectedUserExperienceAtom),
    );
    set(selectedDateAtom, selectedDate);
    set(
      sortFilterAtom,
      get(modeAtom) === 'RECENT_VISITS'
        ? SortFilterEnum.APPOINTMENT_TIME_EARLIEST
        : get(sortFilterAtom),
    );
    set(modeAtom, 'DAILY_HUDDLE');
    set(baseSearchAtom, '');
    set(referralsFilterAtom, null);
  },
);
export const selectUserExperienceAtom = atom(
  (get) => get(selectedUserExperienceAtom) ?? SelectedUserExperience.Default,
  (_, set, selectedExperience: string) => {
    set(selectedUserExperienceAtom, selectedExperience);
  },
);
export const viewRecentPatientsAtom = atom(null, (_, set) => {
  set(modeAtom, 'RECENT_VISITS');
  set(selectedCountsFilterAtom, undefined);
  set(selectedDateAtom, null);
  set(sortFilterAtom, RecentVisitsSortFilter.APPOINTMENT_TIME_LATEST);
  set(operatoryFilterAtom, []);
});

export const selectedAnalysesAtom = atom<{
  selectedAnalyses: string[];
  unplannedQuadrantToothNumbers?: (string | null)[] | null;
  patientId?: string;
  findingType?: 'Perio' | 'Resto';
  anchorEl: HTMLDivElement | null;
}>(INITIAL_SELECTED_ANALYSES_STATE);

export const insurancePdfPathAtom = atom('');
export const isInsurancePdfOpenAtom = atom(false);

export const selectJumpToCurrentApptAtom = atom(
  (get) => get(jumpToCurrentApptAtom) ?? SelectJumpToCurrentAppt.Default,
  (get, set, scrollToPreference: SelectJumpToCurrentApptType) => {
    set(jumpToCurrentApptAtom, scrollToPreference);
    get(jumpToCurrentApptAtom) === SelectJumpToCurrentAppt.Default
      ? set(jumpToCurrentApptAtom, SelectJumpToCurrentAppt.Default)
      : set(jumpToCurrentApptAtom, SelectJumpToCurrentAppt.True);
  },
);
export const selectUnifiedIdAtom = atom(
  (get) => get(unifiedIdAtom) ?? '',
  (get, set, setUnifiedId: string) => {
    set(unifiedIdAtom, setUnifiedId);
    get(unifiedIdAtom) === ''
      ? set(unifiedIdAtom, '')
      : set(unifiedIdAtom, setUnifiedId);
  },
);
export const selectTeethAtom = atom(
  (get) => get(selectedTeethAtom) ?? [],
  (get, set, setSelectedTeeth: string[]) => {
    set(selectedTeethAtom, setSelectedTeeth);
    get(selectedTeethAtom) === null
      ? set(selectedTeethAtom, [])
      : set(selectedTeethAtom, setSelectedTeeth);
  },
);

export const userSelectTeethAtom = atom(
  (get) => get(userSelectedTeethAtom) ?? [],
  (get, set, setSelectedTeeth: string[]) => {
    set(userSelectedTeethAtom, setSelectedTeeth);
    get(userSelectedTeethAtom) === null
      ? set(userSelectedTeethAtom, [])
      : set(userSelectedTeethAtom, setSelectedTeeth);
  },
);

export const selectQuadrantsAtom = atom(
  (get) => get(selectedQuadrantsAtom) ?? [],
  (get, set, setSelectedQuadrants: QuadrantNumber[]) => {
    set(selectedQuadrantsAtom, setSelectedQuadrants);
    get(selectedQuadrantsAtom) === null
      ? set(selectedQuadrantsAtom, [])
      : set(selectedQuadrantsAtom, setSelectedQuadrants);
  },
);

export const hasHuddleDataAtom = atom<boolean | undefined>(undefined);

export const lastSyncAtom = atom<string | undefined>(undefined);

const onboardingSlideStorage = createJSONStorage<number>(() => localStorage);
const onboardingCompleteStorage = createJSONStorage<boolean>(
  () => localStorage,
);

// setting -1 to check whether atom has hydrated or not
export const onboardingStepAtom = atomWithStorage(
  'jotai-onboarding-step',
  -1,
  onboardingSlideStorage,
);

export const onboardingCompleteAtom = atomWithStorage(
  'jotai-onboarding-complete',
  false,
  onboardingCompleteStorage,
);

export const selectOnboardingStepAtom = atom(
  (get) => get(onboardingStepAtom) ?? 0,
  (get, set, currentSlide: number) => {
    get(onboardingStepAtom);
    set(onboardingStepAtom, currentSlide);
  },
);

export const selectOnboardingCompleteAtom = atom(
  (get) => get(onboardingCompleteAtom) ?? false,
  (get, set, onboardingComplete: boolean) => {
    get(onboardingCompleteAtom);
    set(onboardingCompleteAtom, onboardingComplete);
  },
);

export const showOnboardingAtom = atom<boolean>(false);

const vdaSliderDisplayStorage = createJSONStorage<boolean>(() => localStorage);

export const vdaSliderDisplayAtom = atomWithStorage(
  'jotai-vda-slider-display',
  false,
  vdaSliderDisplayStorage,
);

export const selectVdaSliderDisplayAtom = atom(
  (get) => get(vdaSliderDisplayAtom) ?? false,
  (get, set, vdaSliderDisplay: boolean) => {
    get(vdaSliderDisplayAtom);
    set(vdaSliderDisplayAtom, vdaSliderDisplay);
  },
);

export const hasNewAnalysesAtom = atom(false);
export const operatoryListAtom = atom<string[]>([]);

const isActionableViewStorage = createJSONStorage<boolean>(() => localStorage);
export const isActionableViewAtom = atomWithStorage(
  'jotai-is-actionable-view',
  true,
  isActionableViewStorage,
);

// Referrals filter atom
export type ReferralsFilter =
  | 'both'
  | 'submitted'
  | 'needs_referral'
  | 'failed'
  | null;
const referralsFilterStorage = createJSONStorage<ReferralsFilter>(
  () => sessionStorage,
);
export const referralsFilterAtom = atomWithStorage<ReferralsFilter>(
  'jotai-referrals-filter',
  null,
  referralsFilterStorage,
);

// Ambient view type (Notes or Templates)
export type AmbientViewType = 'notes' | 'templates';
const ambientViewTypeStorage = createJSONStorage<AmbientViewType>(
  () => sessionStorage,
);
export const ambientViewTypeAtom = atomWithStorage<AmbientViewType>(
  'jotai-ambient-view-type',
  'notes',
  ambientViewTypeStorage,
);

// Templates search atom
export const templatesSearchAtom = atom<string>('');

// Recent Notes view atom - tracks if we're viewing recent notes sorted by creation date
export const recentNotesViewAtom = atom<boolean>(false);

// Write-only atom to set referrals filter and handle date/mode changes
export const setReferralsFilterAtom = atom(
  null,
  (_, set, filter: ReferralsFilter) => {
    set(referralsFilterAtom, filter);

    if (filter === null) {
      // When "All Appointments" is selected, go back to today's daily huddle
      // Use selectDateAtom which automatically handles mode switching and side effects
      // Use startOfDay to get beginning of today, not current moment (fixes timezone issues)
      set(selectDateAtom, startOfDay(new Date()));
      // Clear operatory and provider filters since available options may be different
      set(operatoryFilterAtom, []);
      set(selectedProvidersAtom, []);
    } else {
      // When a referrals filter is active (not null), clear the date, recommendation/task filters, and switch to recent visits mode
      // Use selectedDateAtom directly since we're manually managing the mode and side effects
      set(selectedDateAtom, null);
      set(selectedCountsFilterAtom, undefined);
      set(modeAtom, 'RECENT_VISITS');
      // Clear operatory and provider filters since available options may be different in referrals mode
      set(operatoryFilterAtom, []);
      set(selectedProvidersAtom, []);
    }
  },
);
